/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {createMutationResult, DurationResult} from '@ion/query/src/utils/helpers';

export interface useDeviceSmartSearchProps {
  accountId: number;
  searchKeyword: string;
  searchField?: string;
  includeSubGroups?: boolean;
  enabled?: boolean;
  staleTime?: DurationResult | number;
};

export type UseDeviceSmartSearchResult = {
  devices: Device[];
  loadDevices: (params: useDeviceSmartSearchProps, callbacks) => void;
}

/**
 * The useDeviceSmartSearch hook is used to retrieve devices based on search query.
 *
 * ```typescript
 *
 *  import {useDeviceSmartSearch} from '@ion/packages/query/src/hooks';
 *
 *  const {device} = useDeviceSmartSearch({
 *    accountId,
 *    searchKeyword,
*     includeSubGroups
 *  });
 *
 * ```
 */
export const useDeviceSmartSearch = (): UseDeviceSmartSearchResult => {
  const mutation = useMutation(async(params: useDeviceSmartSearchProps) => {
    const {
      accountId,
      searchKeyword,
      searchField,
      includeSubGroups
    } = params;
    const results = await loadDevicesBySmartSearch({
      accountId,
      includeSubGroups,
      queryString : searchField ? `(${searchField}: (*${searchKeyword}*))` : `(*${searchKeyword}*)`
    });
    return results;
  });

  return createMutationResult(
    mutation, {
      loadDevices : mutation.mutate
    }
  ) as UseDeviceSmartSearchResult;
};

export default useDeviceSmartSearch;
